import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./DashboardStyleTicker.scss";

type DashboardTickerItem = {
  Stock_Ticker?: string;
  Today_Returns?: number | string;
};

function getLogoUrl(symbol: string) {
  const base = process.env.REACT_APP_COMPANY_DOC || "";
  if (!base) return "";
  return `${base}/Marquee_logos/${symbol}.svg`;
}

const DashboardStyleTicker = () => {
  const [stocks, setStocks] = useState<{ symbol: string; change: string; isPositive: boolean }[]>([]);

  useEffect(() => {
    const fetchTickerData = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/summary.json`);
        const summary: DashboardTickerItem[] = data?.summary || [];

        // Same dashboard idea: show most moving stocks by absolute returns.
        const topStocks = [...summary]
          .filter((item) => item?.Stock_Ticker !== undefined && item?.Today_Returns !== undefined)
          .sort(
            (a, b) =>
              Math.abs(Number(b.Today_Returns ?? 0)) - Math.abs(Number(a.Today_Returns ?? 0))
          )
          .slice(0, 10)
          .map((item) => {
            const returns = Number(item.Today_Returns ?? 0);
            return {
              symbol: String(item.Stock_Ticker).split(".")[0],
              change: `${returns >= 0 ? "+" : ""}${returns.toFixed(2)}%`,
              isPositive: returns >= 0,
            };
          });

        setStocks(topStocks);
      } catch (error) {
        console.error("Failed to load dashboard ticker data:", error);
      }
    };

    fetchTickerData();
  }, []);

  const loop = useMemo(() => [...stocks, ...stocks, ...stocks], [stocks]);

  if (!stocks.length) return null;

  return (
    <div className="dashboard-style-ticker" aria-label="market ticker">
      <div className="dst-track">
        {loop.map((item, idx) => (
          <div className="dst-item" key={`${item.symbol}-${idx}`}>
            <img
              className="dst-logo"
              src={getLogoUrl(item.symbol)}
              alt=""
              loading="lazy"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
            <span className="dst-symbol">{item.symbol}</span>
            <span
              className={`dst-change ${
                item.isPositive ? "dst-change--pos" : "dst-change--neg"
              }`}
            >
              {item.change}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardStyleTicker;
